import {editorBlocksToHMBlockNodes} from '@seed-hypermedia/client/editorblock-to-hmblock'
import {EditorToggledStyle, HMBlockChildrenType, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {Button} from '@shm/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@shm/ui/components/popover'
import {
  Code,
  Emphasis,
  HeadingIcon,
  OrderedList,
  Strikethrough,
  Strong,
  Type,
  Underline,
  UnorderedList,
} from '@shm/ui/icons'
import {Tooltip} from '@shm/ui/tooltip'
import {usePopoverState} from '@shm/ui/use-popover-state'
import {cn} from '@shm/ui/utils'
import {CellSelection} from '@tiptap/pm/tables'
import {ChevronDown, FileText, Link, ListChecks, MessageSquare} from 'lucide-react'
import {TextSelection} from 'prosemirror-state'
import {useEffect, useRef, useState} from 'react'
import {BlockNoteEditor, BlockSpec, getBlockInfoFromSelection, PropSchema, updateGroupCommand} from './blocknote/core'
import {getNearestBlockOrCellPos} from './blocknote/core/extensions/Blocks/helpers/getBlockInfoFromPos'
import {getReferenceableRevision} from './blocknote/core/extensions/BlockRevision/BlockRevisionInvalidation'
import {getGroupInfoFromPos} from './blocknote/core/extensions/Blocks/helpers/getGroupInfoFromPos'
import {prosemirrorPosToBlockTextOffset} from './blocknote/core/extensions/RangeSelection/RangeSelectionPlugin'
import {
  BlockTypeDropdownItem,
  FormattingToolbarProps,
  useEditorContentChange,
  useEditorSelectionChange,
} from './blocknote/react'
import {useDraftActions} from './draft-actions-context'
import {useFragmentActions} from './fragment-actions-context'
import {HMLinkToolbarButton} from './hm-toolbar-link-button'
import {MobileLinkToolbarButton} from './mobile-link-toolbar-button'
import {MobileTextMarkerDialog} from './mobile-text-marker-dialog'
import {MobileTextTypeDialog} from './mobile-text-type-dialog'
import {StyleOptionsPanel} from './style-options-panel'
import {TOOLBAR_COLOR_NAMES, type ToolbarColorName} from './toolbar-color-palette'
import {deriveDraftNameFromBlocks, getSelectedFullBlocks, replaceBlocksWithDraftEmbed} from './turn-into-doc'
import {useMobile} from './use-mobile'

/**
 * Ensures a grid has at least columnCount number of children.
 */
function fillGridChildren(tiptap: any, columnCount: number) {
  setTimeout(() => {
    tiptap.commands.command(({state, dispatch}: {state: any; dispatch: any}) => {
      if (!dispatch) return true
      const {group, $pos, depth} = getGroupInfoFromPos(state.selection.from, state)
      if (group.attrs.listType !== 'Grid') return true
      const currentCount = group.childCount
      if (currentCount >= columnCount) return true
      const schema = state.schema
      const tr = state.tr
      // Insert position: just before the closing of the blockChildren node
      const groupStart = $pos.before(depth) + 1 // inside blockChildren
      const insertPos = groupStart + group.content.size
      for (let i = 0; i < columnCount - currentCount; i++) {
        const para = schema.nodes['paragraph'].create()
        const blockNode = schema.nodes['blockNode'].create({}, para)
        tr.insert(insertPos + i * blockNode.nodeSize, blockNode)
      }
      dispatch(tr)
      return true
    })
  })
}

/**
 * Computes blockId, rangeStart, rangeEnd from the current editor selection.
 * Returns null if the selection spans multiple blocks or is empty.
 */
function getSelectionFragment(editor: BlockNoteEditor<any>): {
  blockId: string
  rangeStart: number
  rangeEnd: number
} | null {
  const view = editor._tiptapEditor.view
  const {state} = view
  const {selection} = state

  if (selection.empty) return null

  // Disable copy link and comment on block buttons for multi-cell selections,
  // because they don't have a single addressable container, so anchoring a
  // comment or fragment link to one of the cells would pick only one cell.
  if (selection instanceof CellSelection) return null

  const {$from, $to} = selection
  const from = $from.pos
  const to = $to.pos

  let blockNode: import('prosemirror-model').Node
  let blockBeforePos: number

  try {
    const posInfo = getNearestBlockOrCellPos(state.doc, from)
    blockNode = posInfo.node
    blockBeforePos = posInfo.posBeforeNode
  } catch {
    return null
  }

  // Only single-block selections and inside tables, only single-cell
  // selections.
  try {
    const endInfo = getNearestBlockOrCellPos(state.doc, to)
    if (endInfo.posBeforeNode !== blockBeforePos) return null
  } catch {
    return null
  }

  const blockId: string = blockNode.attrs?.id ?? ''
  if (!blockId) return null

  // Find blockContent start position and revision. A missing revision means
  // the block has not been published yet, or has been semantically edited
  // since publish, so it cannot be referenced.
  let blockContentBeforePos = blockBeforePos
  const firstChild = blockNode.firstChild
  if (blockNode.type?.name === 'tableCell' || blockNode.type?.name === 'tableHeader') {
    if (firstChild) blockContentBeforePos = blockBeforePos + 1
  } else {
    blockNode.forEach((child, offset) => {
      if (child.type.spec.group === 'block') {
        blockContentBeforePos = blockBeforePos + offset + 1
      }
    })
  }

  if (!getReferenceableRevision(blockNode)) return null

  const rangeStart = prosemirrorPosToBlockTextOffset(state.doc, from, blockContentBeforePos)
  const rangeEnd = prosemirrorPosToBlockTextOffset(state.doc, to, blockContentBeforePos)

  return {blockId, rangeStart, rangeEnd}
}

/** Clears the visible browser/ProseMirror text selection after fragment actions. */
function clearEditorTextSelection(editor: BlockNoteEditor<any>) {
  const view = editor._tiptapEditor?.view
  if (view && !view.isDestroyed) {
    const {state} = view
    const pos = Math.min(Math.max(state.selection.to, 0), state.doc.content.size)
    try {
      view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, pos)))
    } catch {
      // Ignore invalid positions; clearing the native selection below is still useful.
    }
  }

  if (typeof window !== 'undefined') {
    window.getSelection()?.removeAllRanges()
  }
}

const toggleStyles = [
  {
    name: 'Bold (Mod+B)',
    icon: <Strong className="size-4" />,
    style: 'bold' as EditorToggledStyle,
  },
  {
    name: 'Italic (Mod+I)',
    icon: <Emphasis className="size-4" />,
    style: 'italic' as EditorToggledStyle,
  },

  {
    name: 'Underline (Mod+U)',
    icon: <Underline className="size-4" />,
    style: 'underline' as EditorToggledStyle,
  },
  {
    name: 'Strikethrough (Mod+Shift+X)',
    icon: <Strikethrough className="size-4" />,
    style: 'strike' as EditorToggledStyle,
  },
  {
    name: 'Code (Mod+E)',
    icon: <Code className="size-4" />,
    style: 'code' as EditorToggledStyle,
  },
]

export const blockDropdownItems: BlockTypeDropdownItem[] = [
  {
    name: 'Paragraph',
    type: 'paragraph',
    icon: Type,
  },
  {
    name: 'Heading',
    type: 'heading',
    icon: <HeadingIcon className="size-4" />,
  },
  {
    name: 'Bullet List',
    type: 'bulletListItem',
    icon: <UnorderedList className="size-4" />,
  },
  {
    name: 'Numbered List',
    type: 'numberedListItem',
    icon: <OrderedList className="size-4" />,
  },
]

function normalizeColorName(value: unknown): ToolbarColorName {
  if (typeof value !== 'string') return 'default'
  return (TOOLBAR_COLOR_NAMES as readonly string[]).includes(value) ? (value as ToolbarColorName) : 'default'
}

export function HMFormattingToolbar<Schema extends Record<string, BlockSpec<string, PropSchema>>>(
  props: FormattingToolbarProps<Schema> & {
    blockTypeDropdownItems?: BlockTypeDropdownItem[]
    // Current document id for the "Turn into doc" button
    docId?: UnpackedHypermediaId
  },
) {
  const fragmentActions = useFragmentActions()
  const draftActions = useDraftActions()
  const onCreateInlineDraft = draftActions?.onCreateInlineDraft
  const canTurnIntoDoc = !!(props.docId && onCreateInlineDraft)
  const [currentGroupType, setCurrentGroupType] = useState<string>('Group')
  const [currentColumnCount, setCurrentColumnCount] = useState<string>('3')
  const [currentBlockType, setCurrentBlockType] = useState<string>('paragraph')
  const [currentTextColor, setCurrentTextColor] = useState<ToolbarColorName>('default')
  const [currentBackgroundColor, setCurrentBackgroundColor] = useState<ToolbarColorName>('default')
  const [currentTextSize, setCurrentTextSize] = useState<string>('')
  const [currentTextFamily, setCurrentTextFamily] = useState<string>('')
  const [isTextMarkerDialogOpen, setIsTextMarkerDialogOpen] = useState(false)
  const [isTextTypeDialogOpen, setIsTextTypeDialogOpen] = useState(false)
  const [hasReferenceableFragment, setHasReferenceableFragment] = useState(() => !!getSelectionFragment(props.editor))
  const stylePopover = usePopoverState()
  // Keep a ref to the latest popover state so the toolbar visibility
  // subscription doesn't need to resubscribe on every render.
  const stylePopoverRef = useRef(stylePopover)
  stylePopoverRef.current = stylePopover

  // Close the style options popover whenever the parent Tippy toolbar wants to hide.
  useEffect(() => {
    const plugin = props.editor.formattingToolbar
    if (!plugin) return
    return plugin.onUpdate((state) => {
      if (!state.show && stylePopoverRef.current.open) {
        stylePopoverRef.current.onOpenChange(false)
      }
    })
  }, [props.editor])
  const isMobile = useMobile()

  useEditorSelectionChange(props.editor, () => {
    const tiptap = props.editor._tiptapEditor
    const {state} = tiptap
    setHasReferenceableFragment(!!getSelectionFragment(props.editor))

    try {
      const groupInfo = getGroupInfoFromPos(state.selection.from, state)
      setCurrentGroupType(groupInfo.group.attrs.listType || 'Group')
      setCurrentColumnCount(String(groupInfo.group.attrs.columnCount || 3))
    } catch {
      setCurrentGroupType('Group')
      setCurrentColumnCount('3')
    }

    try {
      const blockInfo = getBlockInfoFromSelection(state)
      setCurrentBlockType(blockInfo.blockContentType || 'paragraph')
    } catch {
      setCurrentBlockType('paragraph')
    }

    const activeStyles = props.editor.getActiveStyles()
    setCurrentTextColor(normalizeColorName(activeStyles.textColor))
    setCurrentBackgroundColor(normalizeColorName(activeStyles.backgroundColor))
    setCurrentTextSize(typeof activeStyles.textSize === 'string' ? activeStyles.textSize : '')
    setCurrentTextFamily(typeof activeStyles.textFamily === 'string' ? activeStyles.textFamily : '')
  })

  useEditorContentChange(props.editor, () => {
    const activeStyles = props.editor.getActiveStyles()
    setCurrentTextColor(normalizeColorName(activeStyles.textColor))
    setCurrentBackgroundColor(normalizeColorName(activeStyles.backgroundColor))
    setCurrentTextSize(typeof activeStyles.textSize === 'string' ? activeStyles.textSize : '')
    setCurrentTextFamily(typeof activeStyles.textFamily === 'string' ? activeStyles.textFamily : '')
  })

  const handleGroupTypeChange = (listType: string) => {
    if (listType === currentGroupType) return
    const tiptap = props.editor._tiptapEditor
    const {state} = tiptap
    const {$pos, group} = getGroupInfoFromPos(state.selection.from, state)
    tiptap.commands.command(updateGroupCommand($pos.pos, listType as HMBlockChildrenType, false, false, true))
    if (listType === 'Grid') {
      const colCount = group.attrs.columnCount || 3
      if (!group.attrs.columnCount) {
        const info = getGroupInfoFromPos(tiptap.state.selection.from, tiptap.state)
        const tr = tiptap.state.tr
        tr.setNodeAttribute(info.$pos.before(info.depth), 'columnCount', colCount)
        tiptap.view.dispatch(tr)
      }
      setCurrentColumnCount(String(colCount))
      fillGridChildren(tiptap, colCount)
    }
    setCurrentGroupType(listType)
  }

  const handleColumnCountChange = (colCount: string) => {
    if (colCount === currentColumnCount) return
    const tiptap = props.editor._tiptapEditor
    const {$pos, depth} = getGroupInfoFromPos(tiptap.state.selection.from, tiptap.state)
    const newCount = parseInt(colCount, 10)
    const tr = tiptap.state.tr
    tr.setNodeAttribute($pos.before(depth), 'columnCount', newCount)
    tiptap.view.dispatch(tr)
    setCurrentColumnCount(colCount)
    fillGridChildren(tiptap, newCount)
  }

  const handleBlockTypeChange = (blockType: string) => {
    if (blockType === currentBlockType) return
    const tiptap = props.editor._tiptapEditor
    const {state} = tiptap
    const blockInfo = getBlockInfoFromSelection(state)
    props.editor.updateBlock(
      blockInfo.block.node.attrs.id,
      {
        type: blockType,
        props: {},
      },
      blockType === 'code-block',
    )
    setCurrentBlockType(blockType)
  }

  const handleTextMarkerChange = (listType: string) => {
    handleGroupTypeChange(listType)
    setIsTextMarkerDialogOpen(false)
  }

  const handleTextTypeChange = (blockType: string) => {
    handleBlockTypeChange(blockType)
    setIsTextTypeDialogOpen(false)
  }

  /** Apply a text-size annotation to the current
   * selection. Empty string clears the mark. */
  const handleTextSizeChange = (value: string) => {
    if (value) {
      props.editor.addStyles({textSize: value})
    } else {
      props.editor.removeStyles({textSize: true} as any)
    }
    setCurrentTextSize(value)
  }

  /** Apply a font-family annotation to the current
   * selection. Empty string clears the mark. */
  const handleTextFamilyChange = (value: string) => {
    if (value) {
      props.editor.addStyles({textFamily: value})
    } else {
      props.editor.removeStyles({textFamily: true} as any)
    }
    setCurrentTextFamily(value)
  }

  /**
   * Snapshot the selected blocks, create a new child draft
   * seeded with that content, then replace the source blocks
   * with a single draft-embed in place. Bails out on failure
   * without touching the parent so the user never loses content.
   */
  const handleTurnIntoDoc = async () => {
    if (!props.docId || !onCreateInlineDraft) return
    const blocks = getSelectedFullBlocks(props.editor)
    if (!blocks || blocks.length === 0) return

    let initialContent
    try {
      initialContent = editorBlocksToHMBlockNodes(blocks as any)
    } catch (err) {
      console.error('[turn-into-doc] failed to serialize selected blocks:', err)
      return
    }

    const name = deriveDraftNameFromBlocks(blocks)

    try {
      const {draftId} = await onCreateInlineDraft(props.docId, {initialContent, name})
      replaceBlocksWithDraftEmbed(props.editor, blocks, draftId)
    } catch (err) {
      console.error('[turn-into-doc] failed to create inline draft:', err)
    }
  }

  return (
    <>
      <div
        data-testid="formatting-toolbar"
        className="border-border bg-background z-50 w-fit rounded-md border p-1 shadow-md"
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <div className="text-foreground flex flex-col gap-1">
          {/* Row 1 - inline marks and fragment actions */}
          <div className="flex items-center gap-1">
            {toggleStyles.map((item) => (
              <ToggleStyleButton key={item.style} editor={props.editor} toggleStyle={item.style} {...item} />
            ))}

            {/* Link button - different for mobile/desktop */}
            {isMobile && props.editor.isEditable && (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Mention account"
                  onClick={() => props.editor.mentionMenu?.open('account')}
                >
                  @
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Link document"
                  onClick={() => props.editor.mentionMenu?.open('document')}
                >
                  [[
                </Button>
              </>
            )}
            {isMobile ? (
              <MobileLinkToolbarButton editor={props.editor} />
            ) : (
              <div className="relative">
                <HMLinkToolbarButton editor={props.editor} testId="link-button" />
              </div>
            )}

            {/* Fragment actions are only valid for selections inside one
                previously-published block (identified by revision). Per design:
                no separator from marks; these buttons read as secondary icons. */}
            {fragmentActions && hasReferenceableFragment && (
              <>
                <Tooltip content="Comment">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-md hover:bg-black/10 dark:hover:bg-white/10"
                    onClick={() => {
                      const frag = getSelectionFragment(props.editor)
                      if (frag) {
                        fragmentActions.onComment(frag.blockId, frag.rangeStart, frag.rangeEnd)
                        clearEditorTextSelection(props.editor)
                      }
                    }}
                  >
                    <MessageSquare className="size-4" />
                  </Button>
                </Tooltip>
                <Tooltip content="Copy Link">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-md hover:bg-black/10 dark:hover:bg-white/10"
                    onClick={() => {
                      const frag = getSelectionFragment(props.editor)
                      if (frag) {
                        fragmentActions.onCopyFragmentLink(frag.blockId, frag.rangeStart, frag.rangeEnd)
                        clearEditorTextSelection(props.editor)
                      }
                    }}
                  >
                    <Link className="size-4" />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          {/* Row 2 - Style options and Turn into doc */}
          <div className="flex items-center gap-2">
            {isMobile ? (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 shrink-0 hover:bg-black/10 dark:hover:bg-white/10"
                  onClick={() => setIsTextMarkerDialogOpen(true)}
                >
                  <UnorderedList className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 shrink-0 hover:bg-black/10 dark:hover:bg-white/10"
                  onClick={() => setIsTextTypeDialogOpen(true)}
                >
                  <Type className="size-4" />
                </Button>
              </>
            ) : (
              <Popover {...stylePopover}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="style-options-trigger"
                    className="format-toolbar-item h-9 gap-1.5 rounded-md border border-black/10 px-3 text-sm font-normal hover:bg-black/10 dark:border-white/10 dark:hover:bg-white/10"
                  >
                    <ListChecks className="size-4" />
                    <span>Style options</span>
                    <ChevronDown className="size-3.5 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  sideOffset={8}
                  collisionPadding={8}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                  // Keep focus on the editor when a pointer lands on
                  // non-focusable areas of the panel (padding, gaps, section
                  // headings). Focusable controls (buttons, inputs) keep
                  // their native behavior so text selection and keyboard
                  // focus still work if the panel ever gains an input.
                  onPointerDown={(e) => {
                    const target = e.target as HTMLElement | null
                    if (
                      !target?.closest(
                        'button, input, textarea, select, a, [contenteditable=""], [contenteditable="true"]',
                      )
                    ) {
                      e.preventDefault()
                    }
                  }}
                  className="format-toolbar-item bg-background z-[10000] w-[22rem] max-w-[92vw] p-3"
                >
                  <StyleOptionsPanel
                    editor={props.editor}
                    currentBlockType={currentBlockType}
                    currentGroupType={currentGroupType}
                    currentColumnCount={currentColumnCount}
                    currentTextColor={currentTextColor}
                    currentBackgroundColor={currentBackgroundColor}
                    currentTextSize={currentTextSize}
                    currentTextFamily={currentTextFamily}
                    onBlockTypeChange={handleBlockTypeChange}
                    onGroupTypeChange={handleGroupTypeChange}
                    onColumnCountChange={handleColumnCountChange}
                    onTextSizeChange={handleTextSizeChange}
                    onTextFamilyChange={handleTextFamilyChange}
                  />
                </PopoverContent>
              </Popover>
            )}

            {!isMobile && canTurnIntoDoc && (
              <Tooltip content="Move selected blocks into a new child document">
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="turn-into-doc-button"
                  className="h-9 gap-1.5 rounded-md border border-black/10 px-3 text-sm font-normal hover:bg-black/10 dark:border-white/10 dark:hover:bg-white/10"
                  onClick={handleTurnIntoDoc}
                >
                  <FileText className="size-4" />
                  <span>Turn into doc</span>
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Mobile dialogs */}
      {isMobile && (
        <>
          <MobileTextMarkerDialog
            isOpen={isTextMarkerDialogOpen}
            onClose={() => setIsTextMarkerDialogOpen(false)}
            currentValue={currentGroupType}
            onChange={handleTextMarkerChange}
          />
          <MobileTextTypeDialog
            isOpen={isTextTypeDialogOpen}
            onClose={() => setIsTextTypeDialogOpen(false)}
            currentValue={currentBlockType}
            onChange={handleTextTypeChange}
          />
        </>
      )}
    </>
  )
}

function ToggleStyleButton<Schema extends Record<string, BlockSpec<string, PropSchema>>>({
  editor,
  toggleStyle,

  name,
  icon,
}: {
  editor: BlockNoteEditor<Schema>
  toggleStyle: EditorToggledStyle
  name: string
  icon: any
}) {
  const [active, setActive] = useState<boolean>(toggleStyle in editor.getActiveStyles())

  function toggleCurrentStyle() {
    setActive(toggleStyle in editor.getActiveStyles())
  }

  useEditorContentChange(editor, toggleCurrentStyle)
  useEditorSelectionChange(editor, toggleCurrentStyle)

  function handlePress(style: EditorToggledStyle) {
    editor.focus()
    editor.toggleStyles({[toggleStyle]: true})
  }

  return (
    <Tooltip content={name}>
      <Button
        data-testid={`${toggleStyle}-button`}
        type="button"
        size="icon"
        variant="ghost"
        className={cn(
          'rounded-md border border-black/10 dark:border-white/10',
          'hover:bg-black/10 dark:hover:bg-white/10',
          'focus:bg-black/10 dark:focus:bg-white/10',
          'format-toolbar-item',
          active &&
            'bg-black text-white hover:bg-black/80 hover:text-white dark:bg-white dark:text-black dark:hover:bg-white/90 dark:hover:text-white',
        )}
        onClick={() => {
          console.log('toggleStyle', toggleStyle)
          handlePress(toggleStyle)
        }}
      >
        {icon}
      </Button>
    </Tooltip>
  )
}
